<template>
  <WdsButton id="ai-chat-trigger" secondary :disabled="loading" @click="openChat">
    <WdsStack align-center gap-sm>
      <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT" :size="18" />
      <slot />
    </WdsStack>
  </WdsButton>
  <WdsModal :show md class="ai-chat-modal !tw-overflow-y-hidden" @cancel="show = false">
    <template #header>
      <WdsStack gap-sm align-center>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" />
        <WdsHeading class="tw-my-0" type="title2">
          {{ $t('Worksome Assistant') }}
        </WdsHeading>
        <div>
          <WdsLabel xs warning subtle>
            {{ $t('Beta') }}
            <WdsIcon
              :size="13"
              weight="thin"
              :name="WdsIconNameEnum.LIGHT"
              class="!tw-inline-block"
            />
          </WdsLabel>
        </div>
      </WdsStack>
    </template>
    <div v-if="showBetaTerms" class="tw-max-h-[70vh] tw-overflow-y-auto">
      <p><strong>Experience Worksome Intelligence Beta</strong></p>
      <p>
        We're excited to have you try our AI-powered Beta!
        <br />
        It's designed to enhance your workflow, but since it's still evolving, we want you to be
        aware of a few key points:
      </p>
      <ul>
        <li>
          The Beta is provided
          <strong>as is</strong>
          , with no guarantees. We may improve, change, or remove it anytime.
        </li>
        <li>
          <strong>Don't rely on it</strong>
          for critical business decisions just yet.
        </li>
      </ul>
      <p><strong>Liability &amp; Data</strong></p>
      <ul>
        <li>
          Our liability is
          <strong>limited</strong>
          —see the
          <a
            href="https://storage.worksome.com/platform/files/Worksome-Beta-User-Agreement_20250224_v1.0.pdf"
            target="_blank"
          >
            Beta Terms
          </a>
          .
        </li>
        <li>
          Data may be processed by third parties.
          <strong>Don't submit sensitive info</strong>
          unless you have proper consent.
        </li>
      </ul>
      <p>
        Learn more about
        <a href="https://worksome.notion.site/worksome-intelligence" target="_blank">
          how Worksome uses AI
        </a>
        .
      </p>
      <p>
        By clicking
        <strong>“I Agree”</strong>
        , you accept the
        <a
          href="https://storage.worksome.com/platform/files/Worksome-Beta-User-Agreement_20250224_v1.0.pdf"
          target="_blank"
        >
          Beta Terms
        </a>
        .
      </p>
      <div class="tw-flex tw-justify-center tw-mt-[20px]">
        <button type="button" class="btn btn-primary" @click="handleAiBetaTerms(true)">
          I agree
        </button>
      </div>
    </div>
    <div v-if="betaTermsAgreed" class="tw-h-full tw-w-full">
      <WdsDivider class="tw-flex tw-pb-4 tw-text-gray-700">{{ $t('Chat started') }}</WdsDivider>
      <div
        class="tw-flex tw-flex-col tw-gap-2 tw-mb-4 tw-border tw-border-gray-300 tw-rounded tw-max-h-[calc(75vh-120px)] tw-overflow-y-auto tw-w-full"
      >
        <MessageList
          :messages="messages"
          :user-avatar-path="userAvatar"
          class="tw-w-full"
          @update:rating="handleRatingUpdate"
        />
      </div>
      <div class="tw-flex tw-justify-start tw-mb-4">
        <WdsButtonGhost xs :disabled="working || !aiThreadUuid" @click="handleClearChat">
          <WdsStack align-center gap-sm>
            <WdsIcon name="erase" :size="14" />
            <span class="tw-text-gray-600">{{ $t('Clear chat') }}</span>
          </WdsStack>
        </WdsButtonGhost>
      </div>
      <form class="tw-max-w-full tw-w-full tw-px-1" @submit.prevent="submit">
        <WdsStack gap-sm justify-center align-center>
          <div class="tw-flex-1 ai-chat-text-input">
            <WdsInput v-model="messageText" :disabled="working" required class="!tw-m-0" />
          </div>
          <WdsButton primary class="tw-flex-0 tw-items-center" :disabled="working">
            <WdsIcon v-if="!working" name="send-alt" :size="18" />
            <WdsSpinner v-if="working" indeterminate />
          </WdsButton>
        </WdsStack>
      </form>
    </div>
  </WdsModal>
</template>

<script lang="ts" setup>
import {
  WdsButton,
  WdsButtonGhost,
  WdsDivider,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsLabel,
  WdsModal,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { nextTick, ref } from 'vue'
import { useStorage } from '@vueuse/core'
import { getRandomAvatarPath } from '@/utils/avatar'
import MessageList from '@/components/LLM/MessageList.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import enums from '@/utils/enum'
import { ai } from '@/api/vue-api'
import { NetworkError } from '@/utils/notification'

interface Props {
  context: {
    type: string
    id: number
    aiThreadUuid?: string
  }
}

interface Message {
  id: string
  uuid?: string
  isAuthored: boolean
  isPending: boolean
  content: string
  rating?: string | null
  timestamp?: string
}

const show = ref(false)

const { context } = defineProps<Props>()

// const userAvatar = computed(() => currentUser.avatar) // TODO: Make this work
const userAvatar = getRandomAvatarPath()

const working = ref(false)
const loading = ref(false)
const aiThreadUuid = ref<string | undefined>(context.aiThreadUuid)
const messageText = ref('')
const messages = ref<Message[]>([
  {
    id: crypto.randomUUID(),
    isAuthored: false,
    isPending: false,
    content: 'Please wait...',
  },
])

const showBetaTerms = ref(true)
const betaTermsAgreed = useStorage('ai-beta-terms-agreed', false)

const mapChatResponse = (response) => {
  aiThreadUuid.value = response.data.ai_thread_uuid

  return response.data.messages.map((m) => ({
    id: crypto.randomUUID(),
    uuid: m.uuid,
    isAuthored: m.role !== 'assistant',
    isPending: false,
    content: m.content,
    rating: m.rating,
    timestamp: m.timestamp,
  }))
}

const initializeChat = async () => {
  working.value = true
  try {
    const response = await ai.askAi(context.type, context.id, null, aiThreadUuid.value || null)
    await nextTick()
    messages.value = mapChatResponse(response)
  } catch (error) {
    messages.value.pop()
    new NetworkError({
      msg: $t('An error occurred while processing the assistants response.'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  } finally {
    working.value = false
  }
}

const handleAiBetaTerms = async (answer) => {
  if (answer) {
    betaTermsAgreed.value = true
    showBetaTerms.value = false
    await initializeChat()
  } else {
    betaTermsAgreed.value = false
    show.value = false
  }
}

const handleRatingUpdate = (messageUuid: string, newRating: string) => {
  messages.value = messages.value.map((message) => {
    if (message.uuid === messageUuid) {
      return { ...message, rating: newRating }
    }
    return message
  })
}

const openChat = async () => {
  show.value = true
  working.value = true

  showBetaTerms.value = !betaTermsAgreed.value

  // Only proceed with API call if terms are agreed
  if (betaTermsAgreed.value) {
    await initializeChat()
  } else {
    working.value = false
  }
}

const handleClearChat = async () => {
  if (!aiThreadUuid.value) return

  try {
    await ai.archiveThread(aiThreadUuid.value)
    messages.value = [
      {
        id: crypto.randomUUID(),
        isAuthored: false,
        isPending: false,
        content: 'Please wait...',
      },
    ]
    aiThreadUuid.value = undefined
    await openChat()
  } catch (error) {
    new NetworkError({
      msg: $t('An error occurred while clearing the chat.'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  }
}

const submit = async () => {
  if (!messageText.value.trim()) {
    return
  }

  show.value = true
  working.value = true

  messages.value.push({
    id: crypto.randomUUID(),
    isAuthored: true,
    isPending: false,
    content: messageText.value,
  })

  messages.value.push({
    id: crypto.randomUUID(),
    isAuthored: false,
    isPending: true,
    content: 'Thinking...',
  })

  const prompt = messageText.value

  messageText.value = ''

  try {
    const response = await ai.askAi(context.type, context.id, prompt, aiThreadUuid.value || null)

    await nextTick()

    messages.value = mapChatResponse(response)
  } catch (error) {
    messages.value.pop()
    new NetworkError({
      msg: $t('An error occurred while processing the assistants response.'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  } finally {
    working.value = false
  }
  await nextTick()

  const promptInput = document.querySelector('.wds-input-label input') as HTMLInputElement

  if (promptInput) {
    promptInput.focus()
  }
}
</script>

<style scoped lang="scss">
/* Fix to make input same height as button */
:deep(.wds-input-label) {
  margin: 0;
}

@media (max-height: 850px) {
  .ai-chat-modal {
    padding: 1rem !important;
  }
}

@media (max-height: 700px) {
  .ai-chat-modal {
    padding: 0.5rem !important;
  }
}

@media (max-height: 500px) {
  .ai-chat-modal {
    padding: 0 !important;
  }
}

@media (max-width: 500px) {
  .ai-chat-text-input {
    max-width: 90%;
  }
  :deep(.wds-input-label) {
    width: auto !important;

    .wds-input {
      min-width: 0 !important;
    }
  }
}

:deep(.wds-modal-header) {
  @apply tw-pb-6;
  @apply tw-border-b;
  @apply tw-border-gray-200;
}
</style>
